import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Search, Target, Rocket, LineChart } from 'lucide-react';
import { PROCESS_STEPS } from '../mock';

const ICONS = [Search, Target, Rocket, LineChart];

/**
 * Scroll-triggered animated process section.
 * A vertical timeline with a progress bar that fills as the user scrolls,
 * step cards that fade + slide in alternating from left/right, and floating
 * animated icons. Replaces the flat 4-card grid with a creative reveal.
 */
export default function ProcessSection() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });
  // Progress bar height maps to scroll — starts at 0, fills to 100%.
  const lineHeight = useTransform(scrollYProgress, [0.1, 0.85], ['0%', '100%']);

  return (
    <section ref={containerRef} className='py-24 relative'>
      <div className='max-w-4xl mx-auto px-6'>
        <div className='text-center mb-20'>
          <div className='eyebrow-pill'>Our Marketing Process</div>
          <h2 className='mt-6 font-heading text-4xl md:text-6xl font-bold leading-[1.05]'>
            <span className='text-white'>How We </span>
            <span className='text-brand-gradient'>Drive Results</span>
          </h2>
        </div>

        {/* Vertical timeline */}
        <div className='relative'>
          {/* Static rail (behind) */}
          <div className='absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-white/10' />
          {/* Animated progress line */}
          <motion.div
            className='absolute left-1/2 -translate-x-1/2 top-0 w-[3px] bg-gradient-to-b from-brand via-accent-pink to-accent-cyan rounded-full shadow-[0_0_20px_rgba(138,43,226,0.6)]'
            style={{ height: lineHeight }}
          />

          {PROCESS_STEPS.map((step, i) => {
            const Icon = ICONS[i] || Rocket;
            const isLeft = i % 2 === 0;
            return (
              <ProcessStep
                key={step.step}
                step={step}
                index={i}
                Icon={Icon}
                isLeft={isLeft}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProcessStep({ step, index, Icon, isLeft }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 85%', 'end 30%'],
  });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 1], [0.15, 1, 1]);
  const x = useTransform(scrollYProgress, [0, 0.3], [isLeft ? -60 : 60, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [0.85, 1]);
  const dotScale = useTransform(scrollYProgress, [0, 0.25], [0.5, 1]);

  return (
    <div ref={ref} className='relative grid grid-cols-2 gap-8 mb-20 items-center'>
      {/* Step number pill on the rail */}
      <motion.div
        className='absolute left-1/2 -translate-x-1/2 z-10 flex flex-col items-center'
        style={{ scale: dotScale }}
      >
        <div className='w-16 h-16 rounded-full bg-void border-4 border-brand shadow-[0_0_25px_rgba(138,43,226,0.55)] flex items-center justify-center'>
          <Icon className='w-6 h-6 text-brand' strokeWidth={2} />
        </div>
        <div className='mt-2 font-accent text-[10px] tracking-[0.2em] uppercase text-brand'>
          Step {step.step}
        </div>
      </motion.div>

      {/* Card */}
      <motion.div
        className={`glass-card rounded-2xl p-7 ${isLeft ? 'col-start-1' : 'col-start-2'}`}
        style={{ opacity, x, scale }}
      >
        <h3 className='font-heading text-2xl md:text-3xl font-bold text-white mb-3'>{step.title}</h3>
        <p className='text-white/70 text-[15px] leading-relaxed font-body'>{step.desc}</p>
      </motion.div>

      {/* Empty side gets a floating accent shape for balance */}
      <motion.div
        className={`hidden md:flex items-center justify-center ${isLeft ? 'col-start-2' : 'col-start-1'}`}
        style={{ opacity }}
      >
        <div className='relative w-40 h-40'>
          <motion.div
            className='absolute inset-0 rounded-full border border-brand/30'
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className='absolute inset-4 rounded-full border border-accent-pink/25'
            animate={{ rotate: -360 }}
            transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className='absolute inset-10 rounded-full bg-gradient-to-br from-brand/30 to-accent-pink/20 blur-md'
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className='absolute inset-0 flex items-center justify-center'>
            <span className='font-heading text-5xl font-bold text-brand-gradient opacity-80'>{step.step}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
